// Shared loading / empty / no-match / error states (design_handoff_round2 Shared Patterns §C).
//  - Loading: skeleton rows the shape of the real list; no spinners.
//  - Empty, "nothing yet": what will show up here, and the next step.
//  - Empty, "no match": names what is filtering, the nearest result if there is one, and Clear.
//  - Error: what failed, what happened to the person's work, the next step, and the raw
//    code small at the bottom.
// Styles: styles/uiStates.css (link it on the page).

import { escapeHTML } from './utilities';

const esc = v => escapeHTML(v == null ? '' : String(v));

/** Skeleton rows. cols: relative widths of the bars in one row, e.g. [1, 3, 2]. */
export function skeletonHTML(rows = 6, cols = [1, 3, 2, 1]) {
  const total = cols.reduce((a, b) => a + b, 0);
  const row = `<div class="ui-skel-row">${cols.map((c, i) => `<span class="ui-skel-bar" style="flex:${c} 1 0;opacity:${i ? 0.75 : 1}"></span>`).join('')}</div>`;
  return `<div class="ui-skel" aria-busy="true" aria-label="Loading" data-cols="${total}">${row.repeat(rows)}</div>`;
}

/** Nothing yet. action: { label, attr } where attr is an attribute string for the button (e.g. 'data-add'). */
export function emptyHTML({ title, body = '', action = null }) {
  return `<div class="ui-state ui-empty" role="status">
    <div class="ui-state-title">${esc(title)}</div>
    ${body ? `<div class="ui-state-body">${esc(body)}</div>` : ''}
    ${action ? `<button type="button" class="ui-state-btn" ${action.attr || ''}>${esc(action.label)}</button>` : ''}
  </div>`;
}

/**
 * No match. filters: what is narrowing the list, e.g. ['search “smith”', 'High Priority'].
 * nearest: optional { label, attr } — a button to the closest thing that does exist.
 */
export function noMatchHTML({ what = 'items', filters = [], nearest = null, clearAttr = 'data-ui-clear', clearLabel = 'Clear filters' }) {
  const list = filters.filter(Boolean);
  return `<div class="ui-state ui-nomatch" role="status">
    <div class="ui-state-title">No ${esc(what)} match${list.length ? '' : ' this view'}</div>
    ${list.length ? `<div class="ui-state-body">Filtered by ${list.map(f => `<b>${esc(f)}</b>`).join(', ')}.</div>` : ''}
    <div class="ui-state-actions">
      ${nearest ? `<button type="button" class="ui-state-btn" ${nearest.attr || ''}>${esc(nearest.label)}</button>` : ''}
      <button type="button" class="ui-state-btn ${nearest ? 'ui-state-btn-quiet' : ''}" ${clearAttr}>${esc(clearLabel)}</button>
    </div>
  </div>`;
}

/** Error. kept: what happened to their work; next: what to do; retry: { label, attr }. */
export function errorHTML({ what, kept = '', next = '', code = '', retry = null }) {
  return `<div class="ui-state ui-error" role="alert">
    <div class="ui-state-title">${esc(what)}</div>
    ${kept ? `<div class="ui-state-body">${esc(kept)}</div>` : ''}
    ${next ? `<div class="ui-state-body">${esc(next)}</div>` : ''}
    ${retry ? `<button type="button" class="ui-state-btn" ${retry.attr || ''}>${esc(retry.label)}</button>` : ''}
    ${code ? `<div class="ui-state-code">${esc(code)}</div>` : ''}
  </div>`;
}

/** The short code to show under an error: Firebase's code, else the message. */
export const errorCode = e => (e && (e.code || e.name) ? `${e.code || e.name}${e.message ? `: ${e.message}` : ''}` : String(e || '')).slice(0, 240);

/**
 * Show a no-match box in `list` whenever it has rows but none are visible, however they
 * got hidden (search, chips, filter panel). Watches the rows' style/class changes.
 *   itemSelector: the rows ('.job-item')
 *   describe(): { what, filters, nearest? } at the moment it shows
 *   onClear(), onNearest(): button handlers
 *   active(): false skips (e.g. the list isn't the one on screen)
 */
export function watchNoMatch(list, { itemSelector, describe, onClear, onNearest, active = () => true }) {
  if (!list || list.dataset.uiNoMatch) return;
  list.dataset.uiNoMatch = '1';
  const box = document.createElement('div');
  box.className = 'ui-nomatch-host';
  box.hidden = true;
  // Inside the list, so it shows in the list's own scroll area (re-added if the list is cleared).
  list.prepend(box);
  let timer = null;
  const paint = () => {
    timer = null;
    const rows = list.querySelectorAll(itemSelector);
    const visible = [...rows].some(r => r.style.display !== 'none' && !r.hidden);
    const show = active() && rows.length > 0 && !visible;
    if (!show) { if (!box.hidden) { box.hidden = true; box.innerHTML = ''; box.dataset.html = ''; } return; }
    if (!box.isConnected || box.parentNode !== list) list.prepend(box);
    const d = describe() || {};
    const html = noMatchHTML({ ...d, nearest: d.nearest ? { label: d.nearest.label, attr: 'data-ui-nearest' } : null });
    if (box.dataset.html !== html) { box.dataset.html = html; box.innerHTML = html; } // our own change re-triggers the observer
    box.hidden = false;
  };
  const schedule = () => { if (!timer) timer = setTimeout(paint, 120); };
  new MutationObserver(schedule).observe(list, { subtree: true, childList: true, attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
  box.addEventListener('click', e => {
    if (e.target.closest('[data-ui-clear]')) onClear && onClear();
    else if (e.target.closest('[data-ui-nearest]')) onNearest && onNearest();
  });
  return { refresh: schedule };
}
